import React, { useState, useRef, useMemo, useEffect } from 'react';
import { partial } from 'lodash';
import { useDebounce } from 'utils/hooks';

import InputWrapper from './InputWrapper';

type InputFormatter = {
  format: (value: number | string) => number | string;
  reverse: (value: string) => string;
};

export type InputInvalidField = {
  id: string;
  label?: string;
  message: string;
};

export type InputSelectCommonProps = {
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelPosition?: 'top' | 'bottom' | 'left' | 'right';
  validator?: (value: InputSelectCommonProps['value']) => void;
  onInvalid?: (id: string, label: string, message: string) => void;
  onValid?: (id: string) => void;
  errorMessageProp?: string;
  debounceTime?: number;
  id: string;
  label: string;
  onChange: (value: InputSelectCommonProps['value']) => void;
  value: string | number;
};

export type InputProps = InputSelectCommonProps &
  Omit<React.ComponentPropsWithoutRef<'input'>, 'onChange' | 'onInvalid'> & {
    formatter?: InputFormatter;
  };

function Input({
  required = false,
  disabled = false,
  className = '',
  type = 'text',
  labelPosition = 'top',
  onInvalid = () => {},
  onValid = () => {},
  debounceTime = 750,
  errorMessageProp,
  validator,
  formatter,
  label,
  value,
  id,
  onChange,
  ...rest
}: InputProps) {
  const debouncedValue = useDebounce(value, debounceTime);
  const isFirstRender = useRef(true);
  const [errorMessage, setErrorMessage] = useState('');

  const partialOnInvalid = partial(onInvalid, id, label);
  const partialOnValid = partial(onValid, id);

  const formattedValue = useMemo(
    () => (formatter ? formatter.format(value) : value),
    [formatter, value]
  );

  // Remove formatting before reporting the input's value
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    let val = e.target.value;
    let numVal: number | undefined;

    if (formatter) {
      val = formatter.reverse(val);
    }

    if (type === 'number') {
      numVal = parseFloat(val);
    }

    onChange(numVal ? numVal : val);
  }

  // Validate the debounced input value, but not on first render
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (validator) {
      try {
        validator(debouncedValue);
        setErrorMessage('');
        partialOnValid();
      } catch (e) {
        setErrorMessage(e.message);
        partialOnInvalid(e.message);
      }
    }
    // FIXME: review this behavior
  }, [debouncedValue, validator]);

  return (
    <InputWrapper
      errorMessage={errorMessageProp || errorMessage || undefined}
      id={id}
      className={className}
      label={label}
      labelPosition={labelPosition}
      required={required}
      disabled={disabled}
    >
      <input
        id={id}
        type={type}
        value={formattedValue}
        onChange={handleChange}
        disabled={disabled}
        {...rest}
      />
    </InputWrapper>
  );
}

export default Input;
